import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  Menu, X, ChevronRight, Phone, Mail, MapPin,
  Beef, Fish, Salad, Apple, ShoppingBasket, Snowflake,
  BadgeDollarSign, Warehouse, Truck, ShieldCheck,
  Hotel, UtensilsCrossed, Coffee, School, ChefHat,
  MessageCircle, Send, Building2, Target, Briefcase, Eye
} from 'lucide-react';
import { contactInfo, products as baseProducts, benefits, clients, heroImages, companyInfo } from '../mock';
import { QuotationForm } from './QuotationForm';
import { ProductCatalogModal } from './ProductCatalogModal';
import { Toaster } from './ui/sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Beef, Fish, Salad, Apple, ShoppingBasket, Snowflake,
  BadgeDollarSign, Warehouse, Truck, ShieldCheck,
  Hotel, UtensilsCrossed, Coffee, School, ChefHat
};

export const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [quotationFormOpen, setQuotationFormOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [products, setProducts] = useState(baseProducts);

  // Fetch admin-managed products and merge with base categories
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await axios.get(`${API}/products/`);
        if (Array.isArray(data) && data.length > 0) {
          // Group admin products by category
          const grouped = {};
          data.forEach((p) => {
            if (!grouped[p.category]) grouped[p.category] = [];
            grouped[p.category].push({
              name: p.name,
              description: p.description,
              image: p.image,
            });
          });
          // Merge admin items into base categories (admin items override)
          const merged = baseProducts.map((cat) => {
            const adminItems = grouped[cat.name];
            return adminItems && adminItems.length > 0
              ? { ...cat, items: adminItems }
              : cat;
          });
          setProducts(merged);
        }
      } catch (err) {
        // Silent fallback to mock data
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
      setMobileMenuOpen(false);
    }
  };

  const handleQuotationRequest = () => {
    setQuotationFormOpen(true);
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${contactInfo.whatsapp}`, '_blank');
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setProductModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-center" richColors />
      <QuotationForm open={quotationFormOpen} onOpenChange={setQuotationFormOpen} />
      <ProductCatalogModal 
        open={productModalOpen} 
        onOpenChange={setProductModalOpen} 
        product={selectedProduct}
        onWhatsAppClick={handleWhatsAppClick}
      />
      
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b-2 border-green-500' 
          : 'bg-gradient-to-r from-blue-900 via-blue-800 to-green-700 shadow-md'
      }`}>
        <div className="flex items-center justify-between h-20 w-full px-4 sm:px-6 lg:px-8">
        
            {/* Logo */}
            <div className="flex items-center space-x-4 shrink-0">
              <div className="rounded-full bg-white p-1.5 shadow-md shrink-0">
            
            
                <img 
                  src="https://customer-assets.emergentagent.com/wingman/b9de27ed-8773-488a-a563-4c1c0e59b6b2/attachments/f9aeca50c38a4b009e0e65d09d91a26d_DYC_Logo_Transparent.png"
                  alt="DYC MARKETING SDN BHD Logo"
                  className="h-12 w-12 object-contain"
                />
              </div>
              <div className="flex flex-col leading-tight whitespace-nowrap pr-12">
                <span className={`text-xl font-bold transition-colors duration-300 ${
                  scrolled ? 'text-blue-900' : 'text-white'
                }`}>DYC MARKETING SDN BHD</span>
                <span className={`text-[10px] font-semibold tracking-wide transition-colors duration-300 ${
                  scrolled ? 'text-green-600' : 'text-yellow-300'
                }`}>202501035841 (1637251-D)</span>
              </div>
            </div>
            <div className="flex-grow" /> 
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {['home', 'about', 'products', 'services', 'news', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                    activeSection === item 
                      ? scrolled 
                        ? 'bg-green-500 text-white shadow-md' 
                        : 'bg-yellow-400 text-blue-900 shadow-md'
                      : scrolled
                        ? 'text-gray-700 hover:bg-green-50 hover:text-green-600'
                        : 'text-white hover:bg-white/20 hover:text-yellow-300'
                  }`}
                  data-testid={`nav-${item}`}
                >
                  {item === 'news' ? 'News' : item.charAt(0).toUpperCase() + item.slice(1)}
                </button>
              ))}
            </nav>

            {/* CTA Button + Mobile Menu Button */}
            <div className="flex items-center space-x-3">
              <Button
                onClick={handleWhatsAppClick}
                size="sm"
                className={`hidden md:flex transition-all duration-300 rounded-full font-semibold ${
                  scrolled 
                    ? 'bg-green-500 hover:bg-green-600 text-white' 
                    : 'bg-yellow-400 hover:bg-yellow-500 text-blue-900'
                }`}
                data-testid="header-whatsapp-btn"
              >
                <MessageCircle size={16} className="mr-1" />
                WhatsApp
              </Button>
              
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`md:hidden transition-colors ${
                  scrolled ? 'text-gray-700 hover:text-green-500' : 'text-white hover:text-yellow-300'
                }`}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <nav className={`md:hidden py-4 border-t ${scrolled ? 'border-gray-200' : 'border-white/20'}`}>
              {['home', 'about', 'products', 'services', 'news', 'contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className={`block w-full text-left px-4 py-3 transition-colors font-medium ${
                    scrolled 
                      ? 'text-gray-700 hover:bg-green-50 hover:text-green-600'
                      : 'text-white hover:bg-white/20 hover:text-yellow-300'
                  }`}
                >
                  {item === 'news' ? 'News' : item.charAt(0).toUpperCase() + item.slice(1)}
                </button>
              ))}
            </nav>
          )}
        
      </header>

      {/* Hero Section */}
      <section id="home" className="relative pt-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://customer-assets.emergentagent.com/job_DYC MARKETING SDN BHD-wholesale/artifacts/hbwzi45r_671669731953208996.jfif" 
            alt="Fresh premium food products" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 to-blue-800/85"></div>
        
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              YOUR TRUSTED<br />
              <span className="text-green-400">ONE-STOP FOOD SUPPLY</span><br />
              PARTNER
            </h1>
            <p className="text-xl text-blue-100 mb-10 max-w-2xl">
              Supplying premium quality food products to restaurants, cafes, and businesses across the region. 
              Quality assured, competitively priced, delivered fresh.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={handleQuotationRequest}
                size="lg" 
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Request Quotation
                <ChevronRight className="ml-2" size={20} />
              </Button>
              <Button 
                onClick={handleWhatsAppClick}
                size="lg" 
                variant="outline" 
                className="bg-white hover:bg-green-50 text-blue-900 border-2 border-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
              >
                <MessageCircle className="mr-2" size={20} />
                WhatsApp Us
              </Button>
            </div>
          </div>
        </div>

        {/* Wave Separator */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Product Showcase Strip */}
      <section className="relative py-12 md:py-16 overflow-hidden bg-white" data-testid="product-showcase-strip">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {products.map((product) => (
              <div 
                key={product.id} 
                className="relative aspect-square group overflow-hidden cursor-pointer rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 ring-1 ring-black/5"
                onClick={() => handleProductClick(product)}
              >
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 via-blue-900/30 to-transparent flex items-end justify-center pb-4 opacity-95 group-hover:opacity-100 transition-opacity">
                  <p className="text-white font-bold text-sm md:text-base tracking-wide uppercase drop-shadow-lg">
                    {product.name}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 md:py-24 lg:py-[100px] bg-gradient-to-br from-yellow-50 via-white to-green-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-green-600 mb-3 tracking-wide">ABOUT US</p>
            <h2 className="text-4xl font-bold text-blue-900 mb-4">Get to Know DYC MARKETING SDN BHD</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Learn more about our company, our vision for the future, and opportunities to grow with us
            </p>
          </div>

          <Tabs defaultValue="profile" className="w-full max-w-5xl mx-auto">
            <TabsList className="grid w-full grid-cols-3 mb-8 bg-blue-50 p-1 h-auto">
              <TabsTrigger 
                value="profile" 
                className="data-[state=active]:bg-blue-900 data-[state=active]:text-white py-3 text-base font-semibold"
                data-testid="tab-company-profile"
              >
                <Building2 className="mr-2 hidden sm:inline" size={18} />
                Company Profile
              </TabsTrigger>
              <TabsTrigger 
                value="mission" 
                className="data-[state=active]:bg-blue-900 data-[state=active]:text-white py-3 text-base font-semibold"
                data-testid="tab-mission-vision"
              >
                <Target className="mr-2 hidden sm:inline" size={18} />
                Mission & Vision
              </TabsTrigger>
              <TabsTrigger 
                value="career" 
                className="data-[state=active]:bg-blue-900 data-[state=active]:text-white py-3 text-base font-semibold"
                data-testid="tab-career"
              >
                <Briefcase className="mr-2 hidden sm:inline" size={18} />
                Career
              </TabsTrigger>
            </TabsList>

            {/* Company Profile */}
            <TabsContent value="profile" className="mt-0">
              <div className="bg-gradient-to-br from-yellow-50 via-orange-50 to-green-50 rounded-2xl p-6 sm:p-10 md:p-12 lg:p-14 border-2 border-green-100" data-testid="content-company-profile">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-center">
                  {/* Text column - 60% on desktop */}
                  <div className="lg:col-span-3 max-w-[600px] mx-auto lg:mx-0">
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-900 mb-5 sm:mb-6 leading-tight">Company Profile</h3>
                    <p className="text-gray-700 text-base sm:text-lg mb-5 leading-[1.8]">
                        <strong>DYC MARKETING SDN BHD </strong> 
                        is a Malaysian-based wholesale food supply company dedicated to serving the food service 
                      industry with excellence.
                    </p>
                    <p className="text-gray-700 text-base sm:text-lg mb-5 leading-[1.8]">
                      We specialize in supplying premium-quality food products including fresh meat, seafood, 
                      vegetables, fruits, groceries, and frozen food to restaurants, cafes, schools, and 
                      catering businesses across the region.
                    </p>
                    <p className="text-gray-700 text-base sm:text-lg leading-[1.8]">
                      Backed by reliable cold storage facilities and a dedicated delivery fleet, we are 
                      committed to consistent quality and dependable service that our clients count on.
                    </p>
                  </div>

                  {/* Image column - 40% on desktop: 2x2 mosaic showcasing categories */}
                  <div className="lg:col-span-2 w-full">
                    <div className="grid grid-cols-2 gap-3 rounded-2xl overflow-hidden shadow-xl shadow-blue-900/10">
                      <div className="relative aspect-square overflow-hidden rounded-2xl group">
                        <img
                          src="https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?q=80&w=600&auto=format&fit=crop"
                          alt="Premium fresh meat cuts"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="relative aspect-square overflow-hidden rounded-2xl group">
                        <img
                          src="https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?q=80&w=600&auto=format&fit=crop"
                          alt="Fresh premium seafood"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="relative aspect-square overflow-hidden rounded-2xl group">
                        <img
                          src="https://images.unsplash.com/photo-1566385101042-1a0aa0c1268c?q=80&w=600&auto=format&fit=crop"
                          alt="Farm-fresh vegetables variety"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="relative aspect-square overflow-hidden rounded-2xl group">
                        <img
                          src="https://images.unsplash.com/photo-1619566636858-adf3ef46400b?q=80&w=600&auto=format&fit=crop"
                          alt="Assorted fresh fruits"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Additional business image showcase */}
                <div className="mt-10 rounded-2xl overflow-hidden shadow-xl relative h-64 md:h-80">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_DYC MARKETING SDN BHD-wholesale/artifacts/54irxlw1_WhatsApp%20Image%202026-06-24%20at%2012.53.16%20%281%29.jpeg" 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-900/60 to-blue-900/30 flex items-center">
                    <div className="p-8 md:p-12 max-w-lg">
                      <p className="text-yellow-300 font-semibold text-sm tracking-wider mb-2">OUR OPERATIONS</p>
                      <h4 className="text-white text-2xl md:text-3xl font-bold mb-3">Cold Storage Excellence</h4>
                      <p className="text-blue-50 leading-relaxed">
                        Our reliable cold storage facilities keep frozen meat and seafood at optimal 
                        temperatures — ensuring maximum freshness and quality from our warehouse to your business.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                  <div className="bg-white rounded-xl p-5 shadow-md text-center hover:shadow-lg transition-shadow border-t-4 border-blue-500">
                    <p className="text-3xl font-bold text-blue-900">100+</p>
                    <p className="text-sm text-gray-600 mt-1">Business Clients</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-md text-center hover:shadow-lg transition-shadow border-t-4 border-green-500">
                    <p className="text-3xl font-bold text-green-600">400+</p>
                    <p className="text-sm text-gray-600 mt-1">Product Range</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-md text-center hover:shadow-lg transition-shadow border-t-4 border-orange-500">
                    <p className="text-3xl font-bold text-blue-900">24/7</p>
                    <p className="text-sm text-gray-600 mt-1">Order Support</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-md text-center hover:shadow-lg transition-shadow border-t-4 border-yellow-500">
                    <p className="text-3xl font-bold text-green-600">Fresh</p>
                    <p className="text-sm text-gray-600 mt-1">Daily Delivery</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Mission & Vision */}
            <TabsContent value="mission" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="content-mission-vision">
                <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white rounded-2xl p-8 md:p-10 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-6">
                    <Target size={32} />
                  </div>
                  <h3 className="text-3xl font-bold mb-4">Our Mission</h3>
                  <p className="text-blue-100 leading-relaxed text-lg">
                    To deliver premium-quality food products with unmatched reliability, empowering restaurants, 
                    cafes, schools, and catering businesses to serve their customers with confidence. We are 
                    committed to competitive pricing, consistent freshness, and building lasting partnerships 
                    based on trust and integrity.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-green-600 to-green-500 text-white rounded-2xl p-8 md:p-10 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-900 rounded-full mb-6">
                    <Eye size={32} />
                  </div>
                  <h3 className="text-3xl font-bold mb-4">Our Vision</h3>
                  <p className="text-green-50 leading-relaxed text-lg">
                    To be Malaysia&apos;s leading and most trusted wholesale food supply partner, recognized for 
                    excellence in quality, service, and innovation. We envision a future where every food 
                    business we serve thrives with the confidence that comes from having a dependable supply 
                    chain partner by their side.
                  </p>
                </div>

                <div className="md:col-span-2 bg-blue-50 rounded-2xl p-8">
                  <h4 className="text-xl font-bold text-blue-900 mb-4 text-center">Our Core Values</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-white rounded-full mb-2">
                        <ShieldCheck className="text-blue-900" size={24} />
                      </div>
                      <p className="font-semibold text-blue-900">Quality</p>
                    </div>
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-white rounded-full mb-2">
                        <Truck className="text-blue-900" size={24} />
                      </div>
                      <p className="font-semibold text-blue-900">Reliability</p>
                    </div>
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-white rounded-full mb-2">
                        <BadgeDollarSign className="text-blue-900" size={24} />
                      </div>
                      <p className="font-semibold text-blue-900">Integrity</p>
                    </div>
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-white rounded-full mb-2">
                        <Warehouse className="text-blue-900" size={24} />
                      </div>
                      <p className="font-semibold text-blue-900">Excellence</p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Career */}
            <TabsContent value="career" className="mt-0">
              <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-8 md:p-12" data-testid="content-career">
                <div className="text-center mb-10">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-900 rounded-full mb-6">
                    <Briefcase className="text-green-400" size={40} />
                  </div>
                  <h3 className="text-3xl font-bold text-blue-900 mb-4">Join Our Growing Team</h3>
                  <p className="text-gray-700 max-w-2xl mx-auto leading-relaxed">
                    At DYC MARKETING SDN BHD, we believe our people are our greatest asset. If you&apos;re 
                    passionate, driven, and want to be part of Malaysia&apos;s trusted food supply company, 
                    we&apos;d love to hear from you.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                  <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4">
                      <Truck className="text-green-600" size={24} />
                    </div>
                    <h4 className="font-bold text-blue-900 mb-2">Delivery Team</h4>
                    <p className="text-sm text-gray-600">
                      Drivers and logistics coordinators for our expanding delivery network
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4">
                      <Warehouse className="text-blue-900" size={24} />
                    </div>
                    <h4 className="font-bold text-blue-900 mb-2">Warehouse Operations</h4>
                    <p className="text-sm text-gray-600">
                      Storage specialists and inventory managers for our cold storage facilities
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4">
                      <UtensilsCrossed className="text-green-600" size={24} />
                    </div>
                    <h4 className="font-bold text-blue-900 mb-2">Sales & Marketing</h4>
                    <p className="text-sm text-gray-600">
                      Business development professionals to grow our client relationships
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 md:p-8 text-center">
                  <p className="text-gray-700 mb-4 font-medium">
                    Interested in joining our team? Send us your resume and cover letter.
                  </p>
                  <a 
                    href={`mailto:${contactInfo.email}?subject=Career Application - DYC MARKETING SDN BHD`}
                    className="inline-flex items-center bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
                    data-testid="career-apply-btn"
                  >
                    <Mail className="mr-2" size={20} />
                    Apply Now
                  </a>
                  <p className="text-sm text-gray-500 mt-4">
                    Email us at <span className="font-semibold text-blue-900">www.dycmarketingenterprise@gmail.com</span>
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-green-600 mb-3 tracking-wide">OUR PRODUCT RANGE</p>
            <h2 className="text-4xl font-bold text-blue-900 mb-4">Our Products</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive range of premium-quality food products for your business needs
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-12">
            {products.map((product) => {
              const IconComponent = iconMap[product.icon];
              return (
                <div 
                  key={product.id} 
                  onClick={() => handleProductClick(product)}
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col h-full border border-gray-200 ring-1 ring-black/5 cursor-pointer"
                  data-testid={`product-card-${product.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {/* Image with wider-angle framing and dark overlay */}
                  <div className="relative h-64 overflow-hidden bg-gray-100">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    {/* Subtle dark overlay for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                    
                    {/* Icon badge */}
                    <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-full w-11 h-11 flex items-center justify-center shadow-lg">
                      {IconComponent && <IconComponent className="text-blue-900" size={20} />}
                    </div>
                    
                    {/* Product name overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="text-2xl font-bold text-white drop-shadow-lg tracking-tight">
                        {product.name}
                      </h3>
                    </div>
                  </div>

                  {/* Card content */}
                  <div className="p-6 flex flex-col flex-grow">
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed min-h-[40px]">
                      {product.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {product.categories.map((cat, idx) => (
                        <span 
                          key={idx}
                          className="text-xs bg-blue-50 text-blue-800 px-3 py-1.5 rounded-full font-medium border border-blue-100"
                        >
                          {cat}
                        </span>
                      ))}
                    </div>
                    <div className="mt-auto pt-3 border-t border-gray-100">
                      <p className="text-sm font-semibold text-green-600 group-hover:text-green-700 flex items-center">
                        View {product.items?.length || 0} products
                        <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose DYC MARKETING SDN BHD Section */}
      <section id="services" className="relative py-20 bg-gradient-to-br from-blue-50 to-green-50 overflow-hidden">
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="grid grid-cols-4 h-full">
            <img src="https://customer-assets.emergentagent.com/job_DYC MARKETING SDN BHD-wholesale/artifacts/j2xam78d_Ikan%20Kembung.jfif" alt="" className="w-full h-full object-cover" />
            <img src="https://customer-assets.emergentagent.com/job_DYC MARKETING SDN BHD-wholesale/artifacts/hbwzi45r_671669731953208996.jfif" alt="" className="w-full h-full object-cover" />
            <img src="https://customer-assets.emergentagent.com/job_DYC MARKETING SDN BHD-wholesale/artifacts/95rjseni_WhatsApp%20Image%202026-06-24%20at%2012.31.12.jpeg" alt="" className="w-full h-full object-cover" />
            <img src="https://customer-assets.emergentagent.com/job_DYC MARKETING SDN BHD-wholesale/artifacts/x48gxjt0_WhatsApp%20Image%202026-06-24%20at%2011.24.19%20%282%29.jpeg" alt="" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-blue-900 mb-4">Why Choose DYC MARKETING SDN BHD</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Your reliable partner for consistent quality and service excellence
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit) => {
              const IconComponent = iconMap[benefit.icon];
              return (
                <div 
                  key={benefit.id}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 text-center group"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6 group-hover:bg-green-500 transition-colors duration-300">
                    {IconComponent && <IconComponent className="text-green-600 group-hover:text-white transition-colors duration-300" size={32} />}
                  </div>
                  <h3 className="text-xl font-bold text-blue-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section id="trusted-by" className="py-20 bg-gradient-to-br from-orange-50 via-white to-yellow-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-blue-900 mb-4">Trusted By</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Serving businesses with pride and excellence
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {clients.map((client) => {
              const IconComponent = iconMap[client.icon];
              return (
                <div 
                  key={client.id}
                  className="bg-gradient-to-br from-blue-50 to-green-50 p-6 rounded-xl text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4">
                    {IconComponent && <IconComponent className="text-blue-900" size={28} />}
                  </div>
                  <h3 className="font-bold text-blue-900 text-lg mb-1">{client.name}</h3>
                  <p className="text-green-600 font-semibold text-2xl">{client.count}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Contact Us</h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              Get in touch with us for quotations, inquiries, or partnership opportunities
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <a 
                href={`tel:${contactInfo.phone.replace(/\s|-/g, '')}`}
                className="block bg-white/10 backdrop-blur-sm p-6 rounded-xl hover:bg-white/20 transition-all duration-300"
                data-testid="phone-link"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-green-500 p-3 rounded-lg">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Phone</h3>
                    <p className="text-blue-100">{contactInfo.phone}</p>
                    <p className="text-green-300 text-sm mt-1">{contactInfo.contactPerson}</p>
                  </div>
                </div>
              </a>

              <a 
                href={`mailto:${contactInfo.email}`}
                className="block bg-white/10 backdrop-blur-sm p-6 rounded-xl hover:bg-white/20 transition-all duration-300"
                data-testid="email-link"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-green-500 p-3 rounded-lg">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Email</h3>
                    <p className="text-blue-100 break-all">dycmarketingenterprise@gmail.com</p> 
                  </div>
                </div>
              </a>

              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl hover:bg-white/20 transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="bg-green-500 p-3 rounded-lg">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Address</h3>
                    <p className="text-blue-100">No.43, Jalan Emas SD 5/1A, Bandar Sri Damansara, 52200 Kuala Lumpur</p> 
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleWhatsAppClick}
                size="lg" 
                className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-6 transition-all duration-300 hover:scale-105"
              >
                <MessageCircle className="mr-2" size={20} />
                WhatsApp For Any Further Inquiries
              </Button>
            </div>

            {/* Map */}
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
              <iframe
                src={contactInfo.mapEmbed}
                width="100%"
                height="400"
                style={{ border: 0, borderRadius: '0.5rem' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="DYC MARKETING SDN BHD Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* News & Update Section */}
      <section id="news" className="py-20 bg-gradient-to-br from-yellow-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-orange-500 mb-3 tracking-wide">STAY UPDATED</p>
            <h2 className="text-4xl font-bold text-blue-900 mb-4">News & Update</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Stay informed with the latest updates from DYC MARKETING SDN BHD
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-blue-500" data-testid="news-card-1">
              <div className="h-48 relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?q=80&w=800" 
                  alt="Small business delivery van" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 bg-yellow-400 text-blue-900 px-3 py-1 rounded-full text-xs font-bold shadow-md">
                  COMING SOON
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-blue-900 mb-2">New Delivery Fleet Expansion</h3>
                <p className="text-gray-600 text-sm">
                  We are expanding our delivery fleet to serve you better with faster and more reliable service across the region.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-green-500" data-testid="news-card-2">
              <div className="h-48 relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=800" 
                  alt="Cold storage food warehouse" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                  COMING SOON
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-blue-900 mb-2">Cold Storage Facility Upgrade</h3>
                <p className="text-gray-600 text-sm">
                  Our upgraded cold storage facility ensures maximum freshness and quality for all our food products.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-orange-500" data-testid="news-card-3">
              <div className="h-48 relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1542838132-92c53300491e?q=80&w=800" 
                  alt="Fresh food quality inspection" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                  COMING SOON
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-blue-900 mb-2">Quality Standards Enhancement</h3>
                <p className="text-gray-600 text-sm">
                  Committed to maintaining the highest food safety and quality standards for our valued clients.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-950 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid-cols-1 md:grid-cols-3 gap-12 text-left">
            <div>
              <div className="flex items-center space-x-3 mb-4" data-testid="footer-logo">
                <div className="bg-white rounded-full p-2 shadow-lg">
                  <img 
                    src="https://customer-assets.emergentagent.com/wingman/b9de27ed-8773-488a-a563-4c1c0e59b6b2/attachments/f9aeca50c38a4b009e0e65d09d91a26d_DYC_Logo_Transparent.png"
                    alt="DYC MARKETING SDN BHD Logo"
                    className="h-36 w-36 object-contain"
                  />
                </div>
                <div className="flex flex-col leading-tight whitespace-nowrap">
                  <span className="text-xl font-bold text-white">DYC MARKETING SDN BHD</span>
                  <span className="text-xs text-green-400 font-semibold tracking-wide">202501035841 (1637251-D)</span>
                </div>
              </div>
            
              <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {['home', 'about', 'products', 'services'].map((item) => (
                  <li key={item}>
                    <button 
                      onClick={() => scrollToSection(item)}
                      className="text-blue-200 hover:text-green-400 transition-colors">

                      {item.charAt(0).toUpperCase() + item.slice(1)}
                    </button>
                  </li>
                ))}
            
              </ul>
            </div>
                
              
              <p className="text-blue-200">
              </p>
            </div>

            <div className="mt-8">
              <h4 className="font-semibold text-lg mb-4">Contact Info</h4>
              <ul className="space-y-2 text-blue-200">
                <li className="flex items-center space-x-2">
                  <Phone size={16} />
                  <a 
                    href={`tel:${contactInfo.phone.replace(/\s|-/g, '')}`}
                    className="hover:text-green-400 transition-colors"
                    data-testid="footer-phone-link">
                    {contactInfo.phone}
                  </a>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail size={16} />
                  <a 
                    href="mailto:dycmarketingenterprise@gmail.com"
                    className="hover:text-green-400 transition-colors break-all"
                    data-testid="footer-email-link">
                    dycmarketingenterprise@gmail.com
                  </a>
                </li>
                <li className="flex items-center space-x-2">
                <Send size={16} />                  
                <a 
                    href="https://www.dyc.com.my" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-400 transition-colors"
                    data-testid="footer-website-link">
                    www.dyc.com.my
                  </a>
                </li>
                <li className="flex items-center space-x-2">
                  <MapPin size={16} />
                  <span>No.43, Jalan Emas SD 5/1A, Bandar Sri Damansara, 52200 Kuala Lumpur</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-blue-800 mt-8 pt-8 text-center text-blue-200">
<p>&copy; 2025 DYC MARKETING SDN BHD. All rights reserved. | <a href="https://www.dyc.com.my" target="_blank" rel="noopener noreferrer" className="hover:text-green-400 transition-colors">www.dyc.com.my</a></p>          </div>
  </footer>
  </div>
  
);
};
